/**
 * CodeField — the business-code input, shared by all four Organization pages.
 *
 * ONE COMPONENT BECAUSE IT IS ONE CONCEPT
 * ---------------------------------------
 * Departments, Positions, Employment Types and Branches all carry the same field
 * with the same rules. Four hand-rolled `TextField`s is how one of them ends up
 * saying "Reference" while another says "Code", and how three of them keep the
 * 50-character limit the fourth forgets (svdy-rules §14 Pillar 3, §10.5).
 *
 * WHAT IT ENFORCES, AND WHAT IT ONLY WARNS ABOUT
 * ----------------------------------------------
 * Mirrors `app/organization/code_validator.py` — the SERVER is the gate, this is
 * the courtesy. Errors block; warnings are shown and do not:
 *
 *   * a purely numeric code (`1001`) is a real cost-centre code, so it is accepted
 *     with a caution that a spreadsheet may drop its leading zeros;
 *   * a date-shaped code (`1-2`) is accepted with a caution that Excel may convert
 *     it.
 *
 * Rejecting either would refuse data this product's own customers use.
 *
 * BLANK IS NOT INVALID
 * --------------------
 * Leaving the field empty means "generate one" (ADR-043 D2), which is the fast
 * path an SMB actually wants: type a name, get a department. The placeholder says
 * so, rather than leaving the admin to guess whether blank is allowed.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, InputAdornment, TextField, Tooltip } from '@mui/material';

/** Mirrors `MAX_LENGTH` in the backend validator. */
export const CODE_MAX_LENGTH = 50;

/** Mirrors `CodeProblem`. These are i18n keys under `org:shared.codeField`. */
export const CODE_PROBLEMS = {
  empty: 'code_empty',
  tooLong: 'code_too_long',
  badCharacters: 'code_bad_characters',
  leadingSeparator: 'code_leading_separator',
  looksNumeric: 'code_looks_numeric',
  looksLikeADate: 'code_looks_like_a_date',
} as const;

const ERRORS: readonly string[] = [
  CODE_PROBLEMS.empty,
  CODE_PROBLEMS.tooLong,
  CODE_PROBLEMS.badCharacters,
  CODE_PROBLEMS.leadingSeparator,
];

const VALID = /^[A-Za-z0-9][A-Za-z0-9._\-/]*$/;
const FORMULA_STARTS = ['-', '+', '=', '@'];
const DATE_SHAPED = /^\d{1,4}[-/.]\d{1,2}([-/.]\d{1,4})?$/;

/**
 * Problems with `value`, most serious first. Empty array = nothing to say.
 *
 * Deliberately the same order of checks as the backend: the formula rule is tested
 * before the charset one, because `-1` breaks both and only one of them tells the
 * admin what to do about it.
 */
export function checkCode(value: string): string[] {
  if (!value) return [];
  if (!value.trim()) return [CODE_PROBLEMS.empty];

  const problems: string[] = [];
  if (FORMULA_STARTS.includes(value[0])) {
    problems.push(CODE_PROBLEMS.leadingSeparator);
  } else if (!VALID.test(value)) {
    problems.push(CODE_PROBLEMS.badCharacters);
  }
  if (value.length > CODE_MAX_LENGTH) problems.push(CODE_PROBLEMS.tooLong);
  if (problems.length) return problems;

  if (DATE_SHAPED.test(value)) return [CODE_PROBLEMS.looksLikeADate];
  if (/^\d+$/.test(value)) return [CODE_PROBLEMS.looksNumeric];
  return [];
}

/** True when nothing in `value` is an error. A warning does not make it invalid. */
export function isCodeValid(value: string): boolean {
  return !checkCode(value).some((p) => ERRORS.includes(p));
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  /**
   * `'generated'` marks the code as machine-made: the admin may overwrite it and
   * we will not touch it again (ADR-043 D3).
   */
  codeSource?: string | null;
  disabled?: boolean;
  name?: string;
}

export const CodeField: React.FC<Props> = ({
  value,
  onChange,
  codeSource,
  disabled,
  name = 'code',
}) => {
  const { t } = useTranslation();
  const problems = checkCode(value);
  const problem = problems[0];
  const isError = problem !== undefined && ERRORS.includes(problem);
  const isGenerated = codeSource === 'generated' && Boolean(value);

  return (
    <TextField
      margin="dense"
      name={name}
      label={t('org:shared.codeField.label')}
      placeholder={t('org:shared.codeField.placeholder')}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      fullWidth
      variant="outlined"
      error={isError}
      sx={{ mb: 2 }}
      /*
        The generated marker is an ADORNMENT, not a chip below the field.
        A chip added a third element and a second baseline to a two-line control,
        and svdy-ui §15's one-alignment-axis rule bites: the field's own text sits
        at one inner edge and a chip under it sat at another. Inside the input the
        marker is on the value's own baseline, reads as belonging to the value, and
        costs no layout at all.
      */
      InputProps={
        isGenerated
          ? {
              endAdornment: (
                <Tooltip title={t('org:shared.codeField.generatedTooltip')}>
                  <InputAdornment position="end">
                    <Box
                      component="span"
                      sx={{
                        color: 'text.disabled',
                        fontSize: '0.7rem',
                        fontWeight: 400,
                        cursor: 'help',
                        userSelect: 'none',
                      }}
                    >
                      {t('org:shared.codeField.generatedMarker')}
                    </Box>
                  </InputAdornment>
                </Tooltip>
              ),
            }
          : undefined
      }
      /*
        The specific rule, not the generic sentence: "can't start with - + = or @"
        is worth more under the field than "that code can't be used".
      */
      helperText={
        problem
          ? t(`org:shared.codeField.${problem}`, {
              max: CODE_MAX_LENGTH,
              value,
              stripped: value.replace(/^0+/, '') || '0',
            })
          : t('org:shared.codeField.helper')
      }
      // No `maxLength`: silently truncating a pasted code is worse than saying it
      // is too long, because the admin cannot see what was dropped.
      inputProps={{ 'aria-describedby': `${name}-helper` }}
    />
  );
};

export default CodeField;
